<template>
    <page-container>
        <page-header>
            Outbound Messages
        </page-header>
        <div class="vx-flex vx-flex-col vx-gap-6 xl:vx-flex-row">
            <div class="vx-shrink-0 xl:vx-w-60">
                <analytics-outbound-messages-sidebar v-model="fetchParams"></analytics-outbound-messages-sidebar>
            </div>

            <div class="vx-w-full vx-relative">
                <transition enter-from-class="vx-opacity-0 -vx-translate-y-4"
                    enter-to-class="vx-opacity-100 vx-translate-y-0"
                    leave-from-class="vx-opacity-100 vx-translate-y-0"
                    leave-to-class="vx-opacity-0 -vx-translate-y-4">
                    <div v-if="showLoadingBars"
                        class="vx-absolute vx-w-full vx-transition vx-pt-20">
                        <loading-bars class=""></loading-bars>
                    </div>
                </transition>

                <div class="vx-absolute vx-w-full vx-transition vx-z-[1]"
                    :class="{
                        'vx-opacity-0': showLoadingBars || chartHasValue
                    }">
                    <vx-box color="info"
                        class="vx-flex vx-flex-col vx-px-8 vx-py-20 vx-gap-6 vx-items-center vx-text-xl vx-text-slate-500">
                        <div class="vx-mx-auto vx-text-center">
                            No messages match your current filters.
                        </div>
                        <vx-button type="button"
                            color="primary"
                            size="lg"
                            @click="resetFilters">
                            Reset Filters
                            <font-awesome-icon :icon="faArrowRotateLeft" />
                        </vx-button>
                    </vx-box>
                </div>

                <div class="vx-transition vx-duration-300 vx-grid vx-gap-6 vx-grid-cols-1 sm:vx-grid-cols-12"
                    :class="{
                        'vx-opacity-0': showLoadingBars || !chartHasValue,
                        'vx-opacity-100 vx-delay-200': !(showLoadingBars || !chartHasValue),
                    }">
                    <component v-for="(chart, chartIndex) in charts.filter(chart => !chart.hidden)"
                        ref="chartsRef"
                        :is="chart.component"
                        :key="chart.component.__name + chartIndex"
                        v-bind="chart.attributes"
                        :fetchParams="fetchParams">
                    </component>
                </div>
            </div>
        </div>
    </page-container>
</template>

<script setup>
import PageHeader from '~/components/general/PageHeader.vue';
import PageContainer from '~/components/general/PageContainer.vue';
import { computed, ref, watch } from 'vue';
import { faArrowRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { VxBox, VxButton } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import LoadingBars from '../general/LoadingBars.vue';
import AnalyticsOutboundMessagesSidebar from './AnalyticsOutboundMessagesSidebar.vue';
import AnalyticsOutboundMessagesTotalActiveResourcesByResourceType from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalActiveResourcesByResourceType.vue';
import AnalyticsOutboundMessagesTotalContactsMessaged from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalContactsMessaged.vue';
import AnalyticsOutboundMessagesTotalMessagesSentByMessageType from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalMessagesSentByMessageType.vue';
import AnalyticsOutboundMessagesDeliveryRate from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesDeliveryRate.vue';
import AnalyticsOutboundMessagesClickThroughRate from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesClickThroughRate.vue';
import AnalyticsOutboundMessagesMessageReplyRate from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesMessageReplyRate.vue';
import AnalyticsOutboundMessagesOptOutRate from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesOptOutRate.vue';
import { getDateRange } from '~/utils/analytics';

const defaultFilters = () => {
    const last30Days = getDateRange('last_30_days');
    return {
        range: [last30Days.start_date, last30Days.end_date],
        filters: {
            audience_types: ['marketing', 'transactional', 'acquisition', 're_acquisition'],
            resource_types: undefined,
            resources: undefined,
        },
    };
};

const fetchParams = ref(defaultFilters());

const activeResourcesByResourceTypeHidden = computed(() => {
    return fetchParams.value.filters.resource_types?.includes?.('other');
});

const resetFilters = () => {
    fetchParams.value = defaultFilters();
};

const resourceTypeIsPlaybookOrUser = computed(() => fetchParams.value.filters.resource_types?.includes('playbook') || fetchParams.value.filters.resource_types?.includes('user'))

const charts = computed(() => ([
    {
        component: AnalyticsOutboundMessagesTotalActiveResourcesByResourceType,
        hidden: activeResourcesByResourceTypeHidden.value,
        attributes: {
            class: 'sm:vx-col-span-6 lg:vx-col-span-3',
            resourceType: 'user',
            minHeight: '128px',
        }
    },
    {
        component: AnalyticsOutboundMessagesTotalActiveResourcesByResourceType,
        hidden: activeResourcesByResourceTypeHidden.value,
        attributes: {
            class: 'sm:vx-col-span-6 lg:vx-col-span-3',
            resourceType: 'campaign',
            minHeight: '128px',
        }
    },
    {
        component: AnalyticsOutboundMessagesTotalActiveResourcesByResourceType,
        hidden: activeResourcesByResourceTypeHidden.value,
        attributes: {
            class: 'sm:vx-col-span-6 lg:vx-col-span-3',
            resourceType: 'automation',
            minHeight: '128px',
        }
    },
    {
        component: AnalyticsOutboundMessagesTotalActiveResourcesByResourceType,
        hidden: activeResourcesByResourceTypeHidden.value,
        attributes: {
            class: 'sm:vx-col-span-6 lg:vx-col-span-3',
            resourceType: 'playbook',
            minHeight: '128px',
        }
    },
    //
    {
        component: AnalyticsOutboundMessagesTotalContactsMessaged,
        attributes: {
            class: 'sm:vx-col-span-6 lg:vx-col-span-3',
        }
    },
    {
        component: AnalyticsOutboundMessagesTotalMessagesSentByMessageType,
        attributes: {
            class: 'sm:vx-col-span-6 lg:vx-col-span-3',
        }
    },
    {
        component: AnalyticsOutboundMessagesTotalMessagesSentByMessageType,
        attributes: {
            messageType: 'sms',
            class: 'sm:vx-col-span-6 lg:vx-col-span-3',
        }
    },
    {
        component: AnalyticsOutboundMessagesTotalMessagesSentByMessageType,
        attributes: {
            messageType: 'mms',
            class: 'sm:vx-col-span-6 lg:vx-col-span-3',
        }
    },
    //
    {
        component: AnalyticsOutboundMessagesDeliveryRate,
        attributes: {
            class: resourceTypeIsPlaybookOrUser.value ? 'sm:vx-col-span-4' : 'sm:vx-col-span-6 lg:vx-col-span-3'
        },
    },
    {
        component: AnalyticsOutboundMessagesClickThroughRate,
        attributes: {
            class: resourceTypeIsPlaybookOrUser.value ? 'sm:vx-col-span-4' : 'sm:vx-col-span-6 lg:vx-col-span-3'
        },
    },
    {
        component: AnalyticsOutboundMessagesMessageReplyRate,
        attributes: {
            class: resourceTypeIsPlaybookOrUser.value ? 'sm:vx-col-span-4' : 'sm:vx-col-span-6 lg:vx-col-span-3'
        },
    },
    {
        component: AnalyticsOutboundMessagesOptOutRate,
        hidden: resourceTypeIsPlaybookOrUser.value,
        attributes: {
            class: 'sm:vx-col-span-6 lg:vx-col-span-3'
        },
    },
]));

const chartsRef = ref([]);

const chartHasValue = computed(() => chartsRef.value?.some(chart => chart.hasValue));
const chartLoading = computed(() => chartsRef.value?.some(chart => chart.loading));

const loading = ref(true);
const showLoadingBars = computed(() => loading.value || (!loading.value && chartLoading.value && !chartHasValue.value))

let loadingTimeout;
watch(fetchParams, () => {
    clearTimeout(loadingTimeout);
    loading.value = true;

    loadingTimeout = setTimeout(() => {
        loading.value = false;
    }, 1500);
}, {
    deep: true,
    immediate: true,
})
</script>
